import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, Wallet, TrendingDown, TrendingUp, ArrowUp, ArrowDown,
  AlertCircle, FileText, Plus, CalendarDays, Loader2,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { brl, StatusBadge } from "@/lib/format";
import { StatCard, Panel, ViewAll, QuoteModal } from "@/components/admin/ui";

const tooltipStyle = {
  background: "#12141A",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 12,
  fontSize: 13,
};

export default function DashboardHome() {
  const [data, setData] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const load = () => api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  const m = data.metrics;
  const total = data.revenue_by_service.reduce((acc, s) => acc + s.value, 0);

  return (
    <div data-testid="dashboard-home">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Olá, Vinicius!</h1>
          <p className="mt-1 text-sm text-zinc-500">Bem-vindo ao painel de controle da Manutentudo Serviços</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-2 rounded-xl border border-white/10 bg-[#11141A] px-4 py-2.5 text-sm text-zinc-300 sm:flex" data-testid="date-range-pill">
            <CalendarDays size={16} className="text-zinc-500" />
            30/07/2026 - 30/07/2026
          </span>
          <button
            onClick={() => setQuoteOpen(true)}
            data-testid="new-quote-button"
            className="flex items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105"
          >
            <Plus size={16} strokeWidth={3} /> Novo Orçamento
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="stats-grid">
        <StatCard testId="stat-faturamento" icon={DollarSign} iconCls="bg-emerald-500/15 text-emerald-400" label="Faturamento (Mês)" value={brl(m.faturamento)} sub={`↑ ${m.faturamento_delta}% em relação ao mês anterior`} />
        <StatCard testId="stat-recebimentos" icon={Wallet} iconCls="bg-emerald-500/15 text-emerald-400" label="Recebimentos (Mês)" value={brl(m.recebimentos)} sub={`↑ ${m.recebimentos_delta}% em relação ao mês anterior`} />
        <StatCard testId="stat-despesas" icon={TrendingDown} iconCls="bg-red-500/15 text-red-400" label="Despesas (Mês)" value={brl(m.despesas)} sub={`↑ ${m.despesas_delta}% em relação ao mês anterior`} subCls="text-red-400" />
        <StatCard testId="stat-lucro" icon={TrendingUp} iconCls="bg-[#FFB800]/15 text-[#FFB800]" label="Lucro Líquido (Mês)" value={brl(m.lucro)} sub={`↑ ${m.lucro_delta}% em relação ao mês anterior`} />
        <StatCard testId="stat-a-receber" icon={ArrowUp} iconCls="bg-blue-500/15 text-blue-400" label="A Receber" value={brl(m.a_receber)} sub={`${m.a_receber_titulos} títulos em aberto`} subCls="text-blue-400" />
        <StatCard testId="stat-em-atraso" icon={AlertCircle} iconCls="bg-red-500/15 text-red-400" label="Em Atraso" value={brl(m.em_atraso)} sub={`${m.em_atraso_titulos} títulos vencidos`} subCls="text-red-400" />
        <StatCard testId="stat-a-pagar" icon={ArrowDown} iconCls="bg-orange-500/15 text-orange-400" label="A Pagar" value={brl(m.a_pagar)} sub={`${m.a_pagar_titulos} títulos em aberto`} subCls="text-orange-400" />
        <StatCard testId="stat-orcamentos" icon={FileText} iconCls="bg-violet-500/15 text-violet-400" label="Orçamentos (Mês)" value={data.quotes_total} sub={`${data.quotes_approved} aprovados`} subCls="text-zinc-500" />
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        <Panel
          title="Faturamento dos últimos 6 meses"
          testId="revenue-chart-panel"
          action={<span className="rounded-lg border border-white/10 px-3.5 py-1.5 text-xs text-zinc-400">Últimos 6 meses</span>}
        >
          <div className="h-[260px]" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly_revenue} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFB800" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FFB800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1E222B" vertical={false} />
                <XAxis dataKey="month" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [brl(v), "Faturamento"]} labelStyle={{ color: "#a1a1aa" }} />
                <Area type="monotone" dataKey="value" stroke="#FFB800" strokeWidth={2.5} fill="url(#rev)" dot={{ fill: "#FFB800", r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Receitas por serviço (Mês)" testId="services-chart-panel">
          <div className="flex flex-col items-center gap-6 sm:flex-row">
            <div className="relative h-[220px] w-[220px] shrink-0" data-testid="services-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.revenue_by_service} dataKey="percent" nameKey="name" innerRadius={68} outerRadius={98} paddingAngle={3} stroke="none">
                    {data.revenue_by_service.map((s, i) => (
                      <Cell key={i} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [`${v}%`, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-xl font-bold text-white">{brl(total)}</span>
                <span className="text-[11px] text-zinc-500">Total</span>
              </div>
            </div>
            <ul className="w-full space-y-3" data-testid="services-legend">
              {data.revenue_by_service.map((s) => (
                <li key={s.name} className="flex items-center gap-3 text-sm">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
                  <span className="flex-1 text-zinc-300">{s.name}</span>
                  <span className="font-semibold text-white">{s.percent}%</span>
                  <span className="w-24 text-right text-zinc-400">{brl(s.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-3">
        <Panel title="Ordens de serviço" testId="orders-panel" action={<Link to="/admindashboard/ordens"><ViewAll /></Link>}>
          <ul className="space-y-4">
            {data.orders.map((o) => (
              <li key={o.id} className="flex items-start justify-between gap-3" data-testid={`order-row-${o.number}`}>
                <div>
                  <p className="text-sm font-semibold text-white">#{o.number}</p>
                  <p className="text-xs text-zinc-500">{o.client} - {o.service}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={o.status} />
                  <p className="mt-1 text-[11px] text-zinc-500">{o.date}</p>
                </div>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Contas a receber (Vencendo)" testId="receivables-panel" action={<Link to="/admindashboard/financeiro"><ViewAll /></Link>}>
          <ul className="space-y-4">
            {data.receivables.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3" data-testid={`receivable-row-${r.client.slice(0, 5)}`}>
                <div>
                  <p className="text-sm font-semibold text-white">{r.client}</p>
                  <p className="text-xs text-zinc-500">{r.service}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-white">{brl(r.value)}</p>
                  <p className="text-[11px] text-emerald-400">{r.due}</p>
                </div>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Contas a pagar (Próximas)" testId="payables-panel" action={<Link to="/admindashboard/financeiro"><ViewAll /></Link>}>
          <ul className="space-y-4">
            {data.payables.map((p) => (
              <li key={p.id} className="flex items-start justify-between gap-3" data-testid={`payable-row-${p.supplier.slice(0, 5)}`}>
                <div>
                  <p className="text-sm font-semibold text-white">{p.supplier}</p>
                  <p className="text-xs text-zinc-500">{p.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-white">{brl(p.value)}</p>
                  <p className="text-[11px] text-red-400">{p.due}</p>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} onCreated={load} />
    </div>
  );
}
